import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return d; }
};

export function DeletedNotesDialog({ open, onOpenChange, notes, onRestore, onRefresh }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return notes.filter(n =>
      (n.title || '').toLowerCase().includes(q) ||
      (n.content || '').toLowerCase().includes(q)
    );
  }, [notes, search]);

  const handleRestore = async (id) => {
    try {
      await onRestore(id);
      toast.success('Note restored');
      onRefresh();
    } catch { toast.error('Failed to restore'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="deleted-notes-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-semibold">Deleted Notes</DialogTitle>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input className="pl-8 h-8 text-[13px]" placeholder="Search deleted notes..." value={search} onChange={e => setSearch(e.target.value)} data-testid="deleted-notes-search" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-center py-6 text-[13px] text-gray-400">No deleted notes found</p>
          ) : (
            <div className="space-y-1.5">
              {filtered.map(note => (
                <div key={note.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border hover:bg-gray-50" data-testid={`deleted-note-${note.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-gray-800 truncate">{note.title}</p>
                    {note.content && <p className="text-[11px] text-gray-500 truncate mt-0.5">{note.content}</p>}
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtDate(note.deleted_at)}</span>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 flex-shrink-0" onClick={() => handleRestore(note.id)} data-testid={`restore-note-${note.id}`}>
                    <Undo2 className="h-3.5 w-3.5 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
